import clsx from 'clsx';

import {
  buildOverallWpsGroupedViolation,
  buildOverallWpsLocationRecords,
  getCorrectiveHint,
  groupViolationsByParameter,
  OVERALL_WPS_HINT,
  type WpsViolationRecord,
} from '../../../utils/wpsViolationModel';
import { WpsParameterComparison } from './WpsParameterComparison';
import { WpsViolationLocationTable } from './WpsViolationLocationTable';

interface WpsViolationSummaryPanelProps {
  violations: WpsViolationRecord[];
  loading?: boolean;
  /** False when get-limits has no row for this weld's job (and no project default). */
  limitsConfigured?: boolean;
  /** Human label for project WPS check method (e.g. "zone average"). */
  checkedUsingLabel?: string;
  /** Override default section heading (e.g. pass-level dialog). */
  sectionTitle?: string;
  sectionSubtitle?: string;
  /**
   * Right-rail teaser: show only the worst parameter group + “+N more”.
   * Full list stays in Open pass summary / pass detail.
   */
  teaser?: boolean;
  /** Opens the pass-summary dialog (same as “Open pass summary”). */
  onOpenPassSummary?: () => void;
}

function renderViolationBlocks(
  groups: ReturnType<typeof groupViolationsByParameter>,
  violations: WpsViolationRecord[],
  sectionTitle = 'WPS violations',
  sectionSubtitle?: string,
  options?: { teaser?: boolean; moreCount?: number; onOpenPassSummary?: () => void },
) {
  const totalLocations = groups.reduce((sum, g) => sum + g.locationCount, 0);
  const subtitle =
    sectionSubtitle ??
    `${totalLocations} location${totalLocations === 1 ? '' : 's'} affected`;
  const hideLocations = Boolean(options?.teaser);

  return (
    <section data-testid={options?.teaser ? 'wps-violation-teaser' : undefined}>
      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-heritage-grey">
        {sectionTitle}
      </h4>
      <p className="mb-3 text-xs text-heritage-grey">{subtitle}</p>

      <div className={clsx(!options?.teaser && groups.length > 2 && 'max-h-[28rem] overflow-y-auto')}>
        {groups.map((group) => {
          const groupKey = `${group.parameterKey}::${group.direction}`;
          const groupRecords = violations.filter(
            (v) => `${v.parameterKey}::${v.direction}` === groupKey,
          );

          return (
            <div key={groupKey}>
              <WpsParameterComparison group={group} hideContextLine />
              {!hideLocations ? (
                <WpsViolationLocationTable
                  records={groupRecords}
                  collapsed={groupRecords.length > 3}
                />
              ) : null}
            </div>
          );
        })}
      </div>

      {options?.teaser && (options.moreCount ?? 0) > 0 ? (
        options.onOpenPassSummary ? (
          <button
            type="button"
            data-testid="wps-more-count"
            onClick={options.onOpenPassSummary}
            className="mt-2 text-left text-xs font-medium text-blueprint-blue underline decoration-blueprint-blue/40 underline-offset-2 hover:text-readable-red hover:decoration-readable-red"
          >
            +{options.moreCount} more — open pass summary for full WPS detail
          </button>
        ) : (
          <p className="mt-2 text-xs text-heritage-grey" data-testid="wps-more-count">
            +{options.moreCount} more — open pass summary for full WPS detail
          </p>
        )
      ) : null}
    </section>
  );
}

export const WpsViolationSummaryPanel = ({
  violations,
  loading = false,
  limitsConfigured = true,
  checkedUsingLabel,
  sectionTitle = 'WPS violations',
  sectionSubtitle,
  teaser = false,
  onOpenPassSummary,
}: WpsViolationSummaryPanelProps) => {
  if (loading) {
    return (
      <section>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-heritage-grey">
          {sectionTitle}
        </h4>
        <p className="text-sm text-heritage-grey">Loading WPS violation detail…</p>
      </section>
    );
  }

  if (!violations.length) {
    if (!limitsConfigured) {
      return (
        <section>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-heritage-grey">
            {sectionTitle}
          </h4>
          <p className="text-sm text-heritage-grey">
            No WPS limits configured for this job/pass. Parameter-level out-of-limit reasons
            cannot be calculated until limits are set for this project.
          </p>
        </section>
      );
    }

    const methodHint = checkedUsingLabel
      ? ` Checked using ${checkedUsingLabel}.`
      : '';
    const overallGroup = {
      ...buildOverallWpsGroupedViolation(),
      hint: `${OVERALL_WPS_HINT}${methodHint}`,
    };
    const overallRecords = buildOverallWpsLocationRecords();
    return renderViolationBlocks(
      [overallGroup],
      overallRecords,
      sectionTitle,
      sectionSubtitle,
      teaser ? { teaser: true, moreCount: 0, onOpenPassSummary } : undefined,
    );
  }

  const allGroups = groupViolationsByParameter(violations);
  if (teaser) {
    const first = allGroups[0];
    if (!first) return null;
    const moreCount = Math.max(0, allGroups.length - 1);
    return renderViolationBlocks(
      [first],
      violations,
      sectionTitle,
      sectionSubtitle ?? 'Worst WPS issue for this weld',
      { teaser: true, moreCount, onOpenPassSummary },
    );
  }

  return renderViolationBlocks(
    allGroups,
    violations,
    sectionTitle,
    sectionSubtitle,
  );
};

export function ViolationRowBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      className={clsx(
        'ml-1.5 inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
        'bg-[rgba(196,0,31,0.08)] text-readable-red',
      )}
    >
      {count}
    </span>
  );
}

export function hintForViolation(v: WpsViolationRecord): string {
  return getCorrectiveHint(v.parameterKey, v.direction);
}
